import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "./pd-badge-item.js";
import { animateFlip, capturePositions } from "../flip-animator.js";
import { DragController } from "./DragController.js";
import { LitElement, css, html, nothing } from "lit";
import { property, query, state } from "lit/decorators.js";
import { pdIcons } from "@progressive-development/pd-icon";
import "@progressive-development/pd-icon/pd-icon";
import { msg, str } from "@lit/localize";
import "@progressive-development/pd-forms/pd-button";
import { classMap } from "lit/directives/class-map.js";
//#region src/pd-badge-order/PdBadgeOrder.ts
var BADGE_SELECTOR = "pd-badge-item";
/**
* Badge order component for selecting and sorting badges.
* Two areas: active (sorted, left/bottom) and available (pool, right/top).
*
* Internally controlled after init — badges and value are owned by this component.
* Use setBadges() / reset() for external updates after init.
*
* @tagname pd-badge-order
*
* @event pd-badge-selection-change - Fired when order or selection changes. Detail: { value: string[], badges: PdBadge[] }
* @event pd-reorder - Fired when only the order changes. Detail: { value: string[] }
* @event pd-badge-edited - Fired when a badge is edited inline. Detail: { badge: PdBadge }
* @event pd-ai-trigger - Fired when AI sparkle icon is clicked. Detail: { value, valueName, aiOptions }
*
* @cssprop --pd-badge-order-gap - Gap between the two areas. Default: 1rem.
* @cssprop --pd-badge-order-min-height - Minimum height of badge lists. Default: 200px.
* @cssprop --pd-badge-transition-duration - FLIP animation duration. Default: 200ms.
*/
var PdBadgeOrder = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.label = "";
		this.valueName = "";
		this.numbered = false;
		this.activeLabel = "";
		this.availableLabel = "";
		this.emptyActiveText = "";
		this.emptyAvailableText = "";
		this.editable = "";
		this.creatable = "";
		this.required = false;
		this.disabled = false;
		this.readonly = false;
		this._badges = [];
		this._activeIds = [];
		this._initialized = false;
		this._error = "";
		this._editingBadgeId = null;
		this._dragController = new DragController(this);
		this._autoEditBadgeId = "";
		this._oldPositions = null;
		this._onBadgeEditStart = (e) => {
			this._editingBadgeId = e.detail.badgeId;
		};
		this._onBadgeEditEnd = (e) => {
			if (this._editingBadgeId === e.detail.badgeId) this._editingBadgeId = null;
		};
		this._onBadgeAction = (e) => {
			const { action, badgeId } = e.detail;
			switch (action) {
				case "add":
					this._addBadge(badgeId);
					break;
				case "remove":
					this._removeBadge(badgeId);
					break;
				case "delete":
					this._deleteBadge(badgeId);
					break;
				case "edit-save": this._editBadge(badgeId, e.detail.title, e.detail.description);
			}
		};
	}
	/** Current active IDs (read-only). */
	get value() {
		return this._activeIds;
	}
	/** Current badges (read-only). */
	get badges() {
		return this._badges;
	}
	/** Set badges and active IDs from outside. Marks as initialized. */
	setBadges(badges, activeIds) {
		this._badges = [...badges];
		this._activeIds = [...activeIds];
		this._initialized = true;
	}
	/** Reset to empty state. Allows initialBadges/initialValue to re-apply on next render. */
	reset() {
		this._badges = [];
		this._activeIds = [];
		this._initialized = false;
		this._error = "";
	}
	/** Validate current state. Sets error message visible if invalid. Returns true if valid. */
	validate() {
		const count = this._activeIds.length;
		if (this.required && count === 0) {
			this._error = msg("Mindestens eine Badge auswählen", { id: "pdContent.badgeOrder.error.required" });
			return false;
		}
		if (this.min !== void 0 && count < this.min) {
			this._error = msg(str`Mindestens ${this.min} Badges auswählen`, { id: "pdContent.badgeOrder.error.min" });
			return false;
		}
		if (this.max !== void 0 && count > this.max) {
			this._error = msg(str`Maximal ${this.max} Badges erlaubt`, { id: "pdContent.badgeOrder.error.max" });
			return false;
		}
		this._error = "";
		return true;
	}
	static {
		this.styles = [css`
      :host {
        display: block;
        --pd-input-field-width: 100%;
      }

      .label-header {
        display: flex;
        align-items: end;
        justify-content: space-between;
        width: 100%;
        box-sizing: border-box;
      }

      .label-header label {
        font-size: var(--pd-input-label-font-size, 0.9em);
        color: var(--pd-input-label-col, inherit);
      }

      .label-icons {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .ai-trigger {
        --pd-icon-size: 22px;
        cursor: pointer;
        opacity: 0.7;
        transition: opacity 0.15s ease-out;
      }
      .ai-trigger:hover,
      .ai-trigger:focus-visible {
        opacity: 1;
      }

      .badge-order-container {
        display: grid;
        /* minmax(0, 1fr) statt 1fr: ohne den expliziten 0er-Floor entspricht
           der min-content der Spalte der vollen Breite des längsten nowrap-
           Titels/Description; Tracks explodieren dann über die Container-
           breite hinaus und text-overflow: ellipsis greift nie. */
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: var(--pd-badge-order-gap, 1rem);
        width: 100%;
        box-sizing: border-box;
      }

      .list-section {
        display: flex;
        flex-direction: column;
        min-width: 0;
        min-height: var(--pd-badge-order-min-height, 200px);
      }

      .section-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 0.5rem;
        min-height: 2rem;
        padding: 0.25rem 0 0.5rem;
        font-family: var(--pd-default-font-title-family, sans-serif);
        font-size: 0.8rem;
        font-weight: 600;
        color: var(--pd-default-font-title-col, #374151);
      }

      .section-title-block {
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
        min-width: 0;
      }

      .section-title-line {
        display: flex;
        align-items: baseline;
        gap: 0.35rem;
      }

      .section-count {
        font-weight: 400;
        color: var(--pd-default-disabled-col, #9ca3af);
        font-size: 0.75rem;
      }

      .section-hint {
        font-weight: 400;
        font-size: 0.7rem;
        color: var(--pd-default-font-muted-col, #6b7280);
        line-height: 1.3;
      }

      .badge-list {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        flex: 1;
        padding: 0;
        border: 0;
        border-radius: 0;
        background: transparent;
        overflow-y: auto;
        position: relative;
        transition: background-color 0.15s ease-out;
      }

      .badge-list.drag-over {
        background: color-mix(
          in srgb,
          var(--pd-badge-drop-highlight, var(--pd-default-col, #3b82f6)) 6%,
          transparent
        );
        border-radius: 8px;
      }

      .empty-hint {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 1;
        min-height: 80px;
        color: var(--pd-default-font-muted-col, #6b7280);
        font-size: 0.8rem;
        text-align: center;
        padding: 1rem;
        border: 1px dashed var(--pd-default-disabled-light-col, #e5e7eb);
        border-radius: 8px;
        background: transparent;
      }

      .drop-indicator {
        height: 2px;
        background: var(
          --pd-badge-drop-highlight,
          var(--pd-default-col, #3b82f6)
        );
        border-radius: 1px;
        transition: opacity 0.15s ease-out;
        flex-shrink: 0;
      }

      .drag-ghost {
        position: fixed;
        pointer-events: none;
        z-index: 9999;
        opacity: 0.9;
        transform: scale(var(--pd-badge-drag-scale, 1.03));
        box-shadow: var(--pd-badge-drag-shadow, 0 8px 24px rgba(0, 0, 0, 0.15));
        will-change: transform;
        width: var(--_ghost-width, 300px);
      }

      :host([disabled]) .badge-order-container {
        opacity: 0.5;
        pointer-events: none;
      }

      :host([readonly]) .badge-order-container {
        pointer-events: none;
      }

      .error-box {
        display: block;
        color: var(--pd-default-error-col, #dc2626);
        background: var(--pd-default-error-light-col, #fef2f2);
        border: 1px solid var(--pd-default-error-col, #dc2626);
        border-radius: 8px;
        width: 100%;
        box-sizing: border-box;
        margin-top: 0.35rem;
      }
      .error-box p {
        margin: 0;
        padding: var(--pd-spacing-xs, 0.25rem) var(--pd-spacing-xs, 0.25rem)
          var(--pd-spacing-xs, 0.25rem) var(--pd-spacing-sm, 0.5rem);
        font-size: 0.8rem;
      }

      @media (max-width: 720px) {
        .badge-order-container {
          grid-template-columns: minmax(0, 1fr);
        }
        .list-section.available-section {
          order: -1;
        }
      }
    `];
	}
	update(changedProps) {
		if (!this._initialized) {
			const hasBadges = changedProps.has("initialBadges") && this.initialBadges?.length;
			const hasValue = changedProps.has("initialValue") && this.initialValue?.length;
			if (hasBadges || hasValue) {
				this._badges = [...this.initialBadges ?? []];
				this._activeIds = [...this.initialValue ?? []];
				this._initialized = true;
			}
		}
		super.update(changedProps);
	}
	updated(changedProps) {
		super.updated(changedProps);
		if (this._oldPositions) {
			const oldPos = this._oldPositions;
			this._oldPositions = null;
			requestAnimationFrame(() => {
				if (this._activeListEl) animateFlip(this._activeListEl, BADGE_SELECTOR, oldPos);
				if (this._availableListEl) animateFlip(this._availableListEl, BADGE_SELECTOR, oldPos);
			});
		}
		if (this._autoEditBadgeId) {
			const id = this._autoEditBadgeId;
			this._autoEditBadgeId = "";
			requestAnimationFrame(() => {
				const selector = `pd-badge-item[data-badge-id="${id}"]`;
				(this._activeListEl?.querySelector(selector) || this._availableListEl?.querySelector(selector))?.startEditing();
			});
		}
	}
	get _activeBadges() {
		const badgeMap = new Map(this._badges.map((b) => [b.id, b]));
		return this._activeIds.map((id) => badgeMap.get(id)).filter((b) => !!b);
	}
	get _availableBadges() {
		const activeIds = new Set(this._activeIds);
		return this._badges.filter((b) => !activeIds.has(b.id));
	}
	_captureBeforeChange() {
		if (this._activeListEl) this._oldPositions = capturePositions(this._activeListEl, BADGE_SELECTOR);
		if (this._availableListEl) {
			const availPositions = capturePositions(this._availableListEl, BADGE_SELECTOR);
			if (this._oldPositions) availPositions.forEach((rect, key) => this._oldPositions.set(key, rect));
			else this._oldPositions = availPositions;
		}
	}
	_createBadge(area = "active") {
		if (this.disabled || this.readonly) return;
		const id = `custom-${Date.now()}`;
		const badge = {
			id,
			title: "",
			custom: true
		};
		this._captureBeforeChange();
		this._badges = [...this._badges, badge];
		if (area === "active") {
			if (this.max !== void 0 && this._activeIds.length >= this.max) return;
			this._activeIds = [...this._activeIds, id];
		}
		this._autoEditBadgeId = id;
		this._fireChangeEvents();
	}
	_addBadge(badgeId) {
		if (this.disabled || this.readonly) return;
		if (this._activeIds.includes(badgeId)) return;
		if (this.max !== void 0 && this._activeIds.length >= this.max) return;
		this._captureBeforeChange();
		this._activeIds = [...this._activeIds, badgeId];
		this._fireChangeEvents();
	}
	_removeBadge(badgeId) {
		if (this.disabled || this.readonly) return;
		this._captureBeforeChange();
		this._activeIds = this._activeIds.filter((id) => id !== badgeId);
		this._fireChangeEvents();
	}
	_deleteBadge(badgeId) {
		if (this.disabled || this.readonly) return;
		const badge = this._badges.find((b) => b.id === badgeId);
		if (!badge?.custom) return;
		this._captureBeforeChange();
		if (this._activeIds.includes(badgeId)) this._activeIds = this._activeIds.filter((id) => id !== badgeId);
		this._badges = this._badges.filter((b) => b.id !== badgeId);
		this.dispatchEvent(new CustomEvent("pd-badge-deleted", {
			detail: { badge },
			bubbles: true,
			composed: true
		}));
		this._fireChangeEvents();
	}
	_editBadge(badgeId, title, description) {
		this._badges = this._badges.map((b) => b.id === badgeId ? {
			...b,
			title,
			description: description || void 0
		} : b);
		this.dispatchEvent(new CustomEvent("pd-badge-edited", {
			detail: { badge: this._badges.find((b) => b.id === badgeId) },
			bubbles: true,
			composed: true
		}));
		this._fireChangeEvents();
	}
	/** Reorder: move badge from oldIndex to newIndex within active list */
	reorderBadge(oldIndex, newIndex) {
		const ids = [...this._activeIds];
		if (oldIndex < 0 || oldIndex >= ids.length || newIndex < 0 || newIndex >= ids.length) return;
		this._captureBeforeChange();
		const [moved] = ids.splice(oldIndex, 1);
		ids.splice(newIndex, 0, moved);
		this._activeIds = ids;
		this.dispatchEvent(new CustomEvent("pd-reorder", {
			detail: { value: ids },
			bubbles: true,
			composed: true
		}));
		this._fireChangeEvents();
	}
	/** Insert a badge from available into active list at a specific index */
	insertBadgeAt(badgeId, index) {
		if (this.disabled || this.readonly) return;
		if (this._activeIds.includes(badgeId)) return;
		if (this.max !== void 0 && this._activeIds.length >= this.max) return;
		this._captureBeforeChange();
		const newIds = [...this._activeIds];
		const insertIdx = Math.max(0, Math.min(index, newIds.length));
		newIds.splice(insertIdx, 0, badgeId);
		this._activeIds = newIds;
		this._fireChangeEvents();
	}
	/** Move a badge from active to available (at drop) */
	deactivateBadge(badgeId) {
		this._removeBadge(badgeId);
	}
	_fireChangeEvents() {
		if (this._error) this.validate();
		const detail = {
			value: this._activeIds,
			badges: this._badges
		};
		this.dispatchEvent(new CustomEvent("pd-badge-selection-change", {
			detail,
			bubbles: true,
			composed: true
		}));
	}
	_onAiTrigger() {
		this.dispatchEvent(new CustomEvent("pd-ai-trigger", {
			bubbles: true,
			composed: true,
			detail: {
				value: JSON.stringify(this._activeIds),
				valueName: this.valueName,
				aiOptions: this.aiOptions
			}
		}));
	}
	_onAiTriggerKeyDown(e) {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			this._onAiTrigger();
		}
	}
	render() {
		const activeBadges = this._activeBadges;
		const availableBadges = this._availableBadges;
		const drag = this._dragController;
		const error = this._error;
		const activeLabel = this.activeLabel || msg("Ausgewählt", { id: "pdContent.badgeOrder.activeLabel" });
		const availableLabel = this.availableLabel || msg("Verfügbar", { id: "pdContent.badgeOrder.availableLabel" });
		const emptyActive = this.emptyActiveText || msg("Badges hierher ziehen", { id: "pdContent.badgeOrder.emptyActive" });
		const emptyAvailable = this.emptyAvailableText || msg("Keine weiteren Badges", { id: "pdContent.badgeOrder.emptyAvailable" });
		const activeHint = msg("Werden in dieser Reihenfolge angezeigt", { id: "pdContent.badgeOrder.activeHint" });
		const availableHint = msg("Ziehe hierher, um zu deaktivieren", { id: "pdContent.badgeOrder.availableHint" });
		const createText = msg("Hinzufügen", { id: "pdContent.badgeOrder.create" });
		return html`
      ${this._renderLabel()}

      <div
        class="badge-order-container"
        part="container"
        @badge-action=${this._onBadgeAction}
        @badge-edit-start=${this._onBadgeEditStart}
        @badge-edit-end=${this._onBadgeEditEnd}
      >
        <!-- Active list -->
        <div class="list-section active-section">
          <div class="section-header">
            <span class="section-title-block">
              <span class="section-title-line">
                <slot name="active-header">${activeLabel}</slot>
                <span class="section-count">(${activeBadges.length})</span>
              </span>
              <span class="section-hint">${activeHint}</span>
            </span>
            ${(this.creatable === "active" || this.creatable === "all") && !this.disabled && !this.readonly ? html`
                    <pd-button
                      outline
                      size="sm"
                      text=${createText}
                      icon=${pdIcons.ICON_ADD}
                      @button-clicked=${() => this._createBadge("active")}
                      part="create-button"
                    ></pd-button>
                  ` : nothing}
          </div>
          <div
            id="active-list"
            class=${classMap({
			"badge-list": true,
			"drag-over": drag.isDragging && drag.dropTarget === "active"
		})}
            part="active-list"
            role="listbox"
            aria-label="${activeLabel} (${activeBadges.length})"
            data-area="active"
            @pointerdown=${drag.onPointerDown}
          >
            ${activeBadges.length > 0 ? activeBadges.map((badge, i) => html`
                      ${drag.isDragging && drag.dropTarget === "active" && drag.insertIndex === i ? html`<div class="drop-indicator"></div>` : nothing}
                      <pd-badge-item
                        .badge=${badge}
                        .index=${i + 1}
                        ?numbered=${this.numbered}
                        ?active=${true}
                        ?editable=${this.editable === "active" || this.editable === "all"}
                        ?disabled=${this.disabled}
                        ?readonly=${this.readonly}
                        ?ghost=${drag.isDragging && drag.draggedId === badge.id}
                        ?dimmed=${this._editingBadgeId !== null && this._editingBadgeId !== badge.id}
                        data-badge-id=${badge.id}
                        role="option"
                        aria-label="${badge.title}, Position ${i + 1}"
                      ></pd-badge-item>
                    `) : html`
                    <div class="empty-hint">
                      <slot name="empty-active">${emptyActive}</slot>
                    </div>
                  `}
            ${drag.isDragging && drag.dropTarget === "active" && drag.insertIndex === activeBadges.length ? html`<div class="drop-indicator"></div>` : nothing}
          </div>
        </div>

        <!-- Available list -->
        <div class="list-section available-section">
          <div class="section-header">
            <span class="section-title-block">
              <span class="section-title-line">
                <slot name="available-header">${availableLabel}</slot>
                <span class="section-count">(${availableBadges.length})</span>
              </span>
              <span class="section-hint">${availableHint}</span>
            </span>
            ${(this.creatable === "available" || this.creatable === "all") && !this.disabled && !this.readonly ? html`
                    <pd-button
                      outline
                      size="sm"
                      text=${createText}
                      icon=${pdIcons.ICON_ADD}
                      @button-clicked=${() => this._createBadge("available")}
                      part="create-button"
                    ></pd-button>
                  ` : nothing}
          </div>
          <div
            id="available-list"
            class=${classMap({
			"badge-list": true,
			"drag-over": drag.isDragging && drag.dropTarget === "available"
		})}
            part="available-list"
            role="listbox"
            aria-label="${availableLabel} (${availableBadges.length})"
            data-area="available"
            @pointerdown=${drag.onPointerDown}
          >
            ${availableBadges.length > 0 ? availableBadges.map((badge) => html`
                      <pd-badge-item
                        .badge=${badge}
                        ?editable=${this.editable === "available" || this.editable === "all"}
                        ?disabled=${this.disabled}
                        ?readonly=${this.readonly}
                        ?dimmed=${this._editingBadgeId !== null && this._editingBadgeId !== badge.id}
                        data-badge-id=${badge.id}
                        role="option"
                        aria-label="${badge.title}"
                      ></pd-badge-item>
                    `) : html`
                    <div class="empty-hint">
                      <slot name="empty-available">${emptyAvailable}</slot>
                    </div>
                  `}
          </div>
        </div>
      </div>

      ${this._renderGhost()}
      ${error ? html`<div class="error-box"><p>${error}</p></div>` : nothing}
    `;
	}
	_renderLabel() {
		if (!this.label && !this.aiOptions) return nothing;
		return html`
      <div class="label-header">
        <label>${this.label}${this.required && this.label ? "*" : ""}</label>
        <div class="label-icons">
          ${this.aiOptions ? html`
                  <pd-icon
                    icon="${pdIcons.ICON_AI_SPARKLE}"
                    activeIcon
                    class="ai-trigger"
                    tabindex="0"
                    role="button"
                    aria-label=${msg("KI-Assistent öffnen", { id: "pdContent.badgeOrder.aiTrigger" })}
                    @click="${this._onAiTrigger}"
                    @keydown="${this._onAiTriggerKeyDown}"
                  ></pd-icon>
                ` : nothing}
        </div>
      </div>
    `;
	}
	_renderGhost() {
		const drag = this._dragController;
		if (!drag.isDragging || !drag.draggedId) return nothing;
		const badge = this._badges.find((b) => b.id === drag.draggedId);
		if (!badge) return nothing;
		const idx = this._activeIds.indexOf(drag.draggedId);
		return html`
      <div
        class="drag-ghost"
        style="left: ${drag.ghostX}px; top: ${drag.ghostY}px;"
      >
        <pd-badge-item
          .badge=${badge}
          .index=${idx >= 0 ? idx + 1 : 0}
          ?numbered=${this.numbered && idx >= 0}
          ?active=${idx >= 0}
          ?dragging=${true}
        ></pd-badge-item>
      </div>
    `;
	}
};
__decorate([property({
	type: Array,
	attribute: false
})], PdBadgeOrder.prototype, "initialBadges", void 0);
__decorate([property({
	type: Array,
	attribute: false
})], PdBadgeOrder.prototype, "initialValue", void 0);
__decorate([property({ type: String })], PdBadgeOrder.prototype, "label", void 0);
__decorate([property({ type: String })], PdBadgeOrder.prototype, "valueName", void 0);
__decorate([property({ type: Number })], PdBadgeOrder.prototype, "min", void 0);
__decorate([property({ type: Number })], PdBadgeOrder.prototype, "max", void 0);
__decorate([property({ type: Boolean })], PdBadgeOrder.prototype, "numbered", void 0);
__decorate([property({
	type: String,
	attribute: "active-label"
})], PdBadgeOrder.prototype, "activeLabel", void 0);
__decorate([property({
	type: String,
	attribute: "available-label"
})], PdBadgeOrder.prototype, "availableLabel", void 0);
__decorate([property({
	type: String,
	attribute: "empty-active-text"
})], PdBadgeOrder.prototype, "emptyActiveText", void 0);
__decorate([property({
	type: String,
	attribute: "empty-available-text"
})], PdBadgeOrder.prototype, "emptyAvailableText", void 0);
__decorate([property({ type: String })], PdBadgeOrder.prototype, "editable", void 0);
__decorate([property({ type: String })], PdBadgeOrder.prototype, "creatable", void 0);
__decorate([property({ type: Boolean })], PdBadgeOrder.prototype, "required", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdBadgeOrder.prototype, "disabled", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdBadgeOrder.prototype, "readonly", void 0);
__decorate([property({
	type: Object,
	attribute: false
})], PdBadgeOrder.prototype, "aiOptions", void 0);
__decorate([state()], PdBadgeOrder.prototype, "_badges", void 0);
__decorate([state()], PdBadgeOrder.prototype, "_activeIds", void 0);
__decorate([state()], PdBadgeOrder.prototype, "_error", void 0);
__decorate([state()], PdBadgeOrder.prototype, "_editingBadgeId", void 0);
__decorate([state()], PdBadgeOrder.prototype, "_dragController", void 0);
__decorate([query("#active-list")], PdBadgeOrder.prototype, "_activeListEl", void 0);
__decorate([query("#available-list")], PdBadgeOrder.prototype, "_availableListEl", void 0);
//#endregion
export { PdBadgeOrder };
